import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { FastField, Field, Form, Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Loader from "../../../components/Loader";
import { getCurrentUrl } from "../../../utils/urls";
import { Module } from "./../../../constants";
import {
  PUBLIC_GROUP_URL,
  PUBLIC_TEST_URL,
  CUSTOM_PUBLIC_GROUP_URL,
  INTRO_SUB_URL,
  START_SESSION_ID_SUB_URL,
} from "../../Frontend/constants";
import { GROUP_URL } from "../constants";

function GroupForm({ testId, group, onSubmit, error, loading, editMode }) {
  const history = useHistory();
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={{
        testId,
        name: "",
        welcomeHtml: "",
        averageLabel: t("tool.groupForm.averageDefaultText", "Average"),
        distributionLabel: t(
          "tool.groupForm.distributionDefaultText",
          "Distribution"
        ),
        expandItemsLabel: t(
          "tool.groupForm.expandItemsLabel",
          "Show item statistics"
        ),
        startDate: "",
        endDate: "",
        cssLink: "",
        participantsLimit: 0,
        onlyCompleted: false,
        ...(group &&
          Object.keys(group).reduce((acc, key) => {
            let value = group[key];
            if (key === "startDate" || key === "endDate") {
              if (value) {
                value = format(new Date(group[key]), "yyyy-MM-dd");
              } else {
                value = ""; // Use empty string for empty field.
              }
            }

            return {
              ...acc,
              [key]: value,
            };
          }, {})),
      }}
      onSubmit={async values => {
        const res = await onSubmit(values);

        if (res) {
          history.push(`${GROUP_URL}/${res._id}`);
        }
      }}
    >
      {({ touched, errors, values }) => {
        return (
          <Form>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12}>
                    <FastField
                      name="name"
                      as={TextField}
                      required
                      label={t("tool.groupForm.testName", "Name / Description")}
                      fullWidth
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="welcomeHtml"
                      label={t("tool.groupForm.welcomeHTML", "Welcome HTML")}
                      as={TextField}
                      multiline
                      rows={4}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>
                      {t("tool.groupForm.averageLabel", "Average")}*
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FastField
                      name="averageLabel"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>
                      {t("tool.groupForm.distributionLabel", "Distribution")}*
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FastField
                      name="distributionLabel"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>
                      {t("tool.groupForm.showItems", "Show items")}*
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FastField
                      name="expandItemsLabel"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3}>
                    <Typography>{t("tool.groupForm.from", "From")}:</Typography>
                  </Grid>
                  <Grid item xs>
                    <Field
                      name="startDate"
                      type="date"
                      as={TextField}
                      error={touched.startDate && Boolean(errors.startDate)}
                      helperText={touched.startDate && errors.startDate}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        style: { minWidth: 175 },
                        max: values.endDate ? values.endDate : undefined,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3}>
                    <Typography>{t("tool.groupForm.to", "To")}:</Typography>
                  </Grid>
                  <Grid item xs>
                    <Field
                      name="endDate"
                      error={touched.endDate && Boolean(errors.endDate)}
                      helperText={touched.endDate && errors.endDate}
                      min={values.startDate ? values.startDate : undefined}
                      type="date"
                      as={TextField}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        style: { minWidth: 175 },
                        min: values.startDate ? values.startDate : undefined,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <FormControlLabel
                      label={t(
                        "tool.groupForm.onlyCompletedTest",
                        "Only completed tests"
                      )}
                      control={
                        <FastField
                          type="checkbox"
                          as={Checkbox}
                          name="onlyCompleted"
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography>
                      {t(
                        "tool.groupForm.participantsLimit",
                        "Participants limit"
                      )}
                      :
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <FastField
                      name="participantsLimit"
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="number"
                      inputProps={{
                        min: 0,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}></Grid>
                  <Grid item xs={3}>
                    <Typography>CSS</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <FastField
                      name="cssLink"
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      error={touched.cssLink && Boolean(errors.cssLink)}
                      helperText={touched.cssLink && errors.cssLink}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box m="20px 0 15px">
                      <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        disabled={loading}
                      >
                        {editMode
                          ? t("tool.addEdit.save", "Save")
                          : t("tool.addEdit.create", "Create")}
                      </Button>
                      <Button
                        color="secondary"
                        onClick={() => {
                          history.push(GROUP_URL);
                        }}
                      >
                        {t("tool.groupForm.close", "Close")}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {error && (
                  <Typography color="secondary">
                    {error?.response?.data?.message ??
                      t(
                        "tool.addEdit.unexpectedError",
                        "Unexpected error occurred"
                      )}
                  </Typography>
                )}
                {loading && <Loader />}
                {group && (
                  <>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={3}>
                        <Typography>ID</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <TextField
                          variant="outlined"
                          disabled
                          defaultValue={group._id}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>
                          {t("tool.groupForm.boardForGroup", "Board for group")}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          size="small"
                          disabled
                          value={getCurrentUrl(
                            `${
                              group.client?.isSpecialReport
                                ? CUSTOM_PUBLIC_GROUP_URL
                                : PUBLIC_GROUP_URL
                            }/${group._id}`
                          )}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>
                          {t(
                            "tool.groupForm.surveyForGroup",
                            "Survey for group"
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          size="small"
                          disabled
                          defaultValue={getCurrentUrl(
                            `${PUBLIC_TEST_URL}/${group.testId}/${START_SESSION_ID_SUB_URL}/${Module.Analytics}/${INTRO_SUB_URL}/?groupId=${group._id}`
                          )}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}

export default GroupForm;
